/**
 * @module AdaptersPage
 * @package @geenius/adapters/react-css
 * @description Renders the vanilla CSS adapters overview for the React package.
 * The page summarizes adapter health, supports status filtering, and provides
 * an accessible modal workflow for configuring each adapter domain.
 */

import type {
  AdapterDomain,
  AdapterStatusType,
  DomainAdapterConfig,
} from "@geenius/adapters";
import {
  ADAPTER_DOMAINS,
  ADAPTER_STATUSES,
  DOMAIN_LABELS,
  getProvidersForDomain,
} from "@geenius/adapters";
import type { JSX } from "react";
import { useEffect, useId, useRef, useState } from "react";
import { AdapterConfigForm } from "../components/AdapterConfigForm";
import { AdapterList } from "../components/AdapterList";
import { AdapterStatusBadge } from "../components/AdapterStatusBadge";
import { useAdapters } from "../hooks";
import { cx } from "../lib/cx";
import { PageErrorBoundary } from "./PageErrorBoundary";

const DIALOG_FOCUSABLE_SELECTORS =
  'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

type RegistryEntry = {
  domain: AdapterDomain;
  provider: string;
  status: AdapterStatusType;
};

/**
 * Props accepted by the React CSS adapters overview page.
 *
 * @property onNavigateDetail Optional callback used instead of opening the inline modal
 * @property onConfigure Optional callback used to persist submitted provider configuration
 * @property configByDomain Optional configuration snapshot used to hydrate modal forms (parity with React variant)
 * @property initialConfigs Back-compat alias for {@link AdaptersPageProps.configByDomain}; `configByDomain` wins when both are set
 * @property className Optional class name merged onto the page root
 * @property registryEntries Optional adapter registry snapshot rendered above the grid
 */
export interface AdaptersPageProps {
  onNavigateDetail?: (domain: AdapterDomain) => void;
  onConfigure?: (
    domain: AdapterDomain,
    config: DomainAdapterConfig,
  ) => Promise<void> | void;
  configByDomain?: Partial<Record<AdapterDomain, DomainAdapterConfig>>;
  initialConfigs?: Partial<Record<AdapterDomain, DomainAdapterConfig>>;
  className?: string;
  registryEntries?: RegistryEntry[];
}

/**
 * Returns focusable descendants for an active dialog container.
 *
 * @param container Dialog container used to scope the focus trap
 * @returns Interactive descendants that should participate in tab order
 */
function getDialogFocusableElements(container: HTMLElement): HTMLElement[] {
  return Array.from(
    container.querySelectorAll<HTMLElement>(DIALOG_FOCUSABLE_SELECTORS),
  );
}

/**
 * Renders the adapter management dashboard for the React CSS variant.
 *
 * @param props Page props controlling navigation overrides and registry data
 * @returns A dashboard with status filters, adapter cards, and a modal editor
 * @example
 * ```tsx
 * <AdaptersPage onNavigateDetail={(domain) => navigate(`/adapters/${domain}`)} />
 * ```
 */
export function AdaptersPage(props: AdaptersPageProps): JSX.Element {
  return (
    <PageErrorBoundary
      className={props.className}
      title="Adapters failed to load"
    >
      <AdaptersPageContent {...props} />
    </PageErrorBoundary>
  );
}

function AdaptersPageContent({
  onNavigateDetail,
  onConfigure,
  configByDomain,
  initialConfigs,
  className = "",
  registryEntries,
}: AdaptersPageProps): JSX.Element {
  const resolvedConfigByDomain = configByDomain ?? initialConfigs;
  const { statuses, isLoading } = useAdapters();
  const [selectedDomain, setSelectedDomain] = useState<AdapterDomain | null>(
    null,
  );
  const [filterStatus, setFilterStatus] = useState<
    AdapterStatusType | undefined
  >();
  const modalRef = useRef<HTMLDivElement | null>(null);
  const modalTitleId = useId();
  const adapterDomainsHeadingId = useId();

  const counts = {
    connected: ADAPTER_DOMAINS.filter(
      (d) => statuses[d]?.status === "connected",
    ).length,
    error: ADAPTER_DOMAINS.filter((d) => statuses[d]?.status === "error")
      .length,
    total: ADAPTER_DOMAINS.length,
  };

  useEffect(() => {
    if (!selectedDomain) {
      return;
    }

    if (typeof window === "undefined" || typeof document === "undefined") {
      return;
    }

    const dialog = modalRef.current;
    if (!dialog) {
      return;
    }

    const ownerDocument = dialog.ownerDocument;
    const previousActiveElement =
      ownerDocument.activeElement instanceof HTMLElement
        ? ownerDocument.activeElement
        : null;
    const focusableElements = getDialogFocusableElements(dialog);
    const firstFocusableElement = focusableElements[0];
    const lastFocusableElement = focusableElements.at(-1);

    (firstFocusableElement ?? dialog).focus();

    const handleKeyDown = (event: KeyboardEvent): void => {
      if (event.key === "Escape") {
        event.preventDefault();
        setSelectedDomain(null);
        return;
      }

      if (event.key !== "Tab") {
        return;
      }

      const activeElement = ownerDocument.activeElement;

      if (!firstFocusableElement || !lastFocusableElement) {
        event.preventDefault();
        dialog.focus();
        return;
      }

      if (event.shiftKey && activeElement === firstFocusableElement) {
        event.preventDefault();
        lastFocusableElement.focus();
        return;
      }

      if (!event.shiftKey && activeElement === lastFocusableElement) {
        event.preventDefault();
        firstFocusableElement.focus();
      }
    };

    ownerDocument.addEventListener("keydown", handleKeyDown);

    return () => {
      ownerDocument.removeEventListener("keydown", handleKeyDown);
      previousActiveElement?.focus();
    };
  }, [selectedDomain]);

  if (isLoading) {
    return (
      <div
        className={cx(
          "gn-adapters-page",
          "gn-adapters-page--wide",
          "gn-adapters-page--loading",
          className,
        )}
      >
        <div
          aria-busy="true"
          aria-label="Loading adapters"
          className="gn-adapters-loading"
          role="status"
        >
          <div className="gn-adapters-skeleton gn-adapters-skeleton--title" />
          <div className="gn-adapters-skeleton gn-adapters-skeleton--subtitle" />
          <div className="gn-adapters-summary">
            {[1, 2, 3].map((i) => (
              <div
                key={i}
                className="gn-adapters-skeleton gn-adapters-skeleton--summary"
              />
            ))}
          </div>
          <div className="gn-adapters-grid">
            {[1, 2, 3, 4].map((i) => (
              <div
                key={i}
                className="gn-adapters-skeleton gn-adapters-skeleton--card"
              />
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div
      className={cx("gn-adapters-page", "gn-adapters-page--wide", className)}
    >
      <div className="gn-adapters-page__header">
        <h1 className="gn-adapters-page__title">Adapters</h1>
        <p className="gn-adapters-page__subtitle">
          Manage your infrastructure adapters - database, auth, AI, storage,
          payments, and admin.
        </p>
      </div>

      <div className="gn-adapters-summary">
        <div className="gn-adapters-summary-card">
          <p className="gn-adapters-summary__label">Total</p>
          <p className="gn-adapters-summary__value">{counts.total}</p>
          <p className="gn-adapters-summary__desc">adapter domains</p>
        </div>
        <div className="gn-adapters-summary-card gn-adapters-summary-card--success">
          <p className="gn-adapters-summary__label">Connected</p>
          <p className="gn-adapters-summary__value">{counts.connected}</p>
          <p className="gn-adapters-summary__desc">active adapters</p>
        </div>
        {counts.error > 0 && (
          <div className="gn-adapters-summary-card gn-adapters-summary-card--error">
            <p className="gn-adapters-summary__label">Errors</p>
            <p className="gn-adapters-summary__value">{counts.error}</p>
            <p className="gn-adapters-summary__desc">need attention</p>
          </div>
        )}
      </div>

      {registryEntries && registryEntries.length > 0 && (
        <section className="gn-adapters-registry">
          <div className="gn-adapters-registry__header">
            <div>
              <p className="gn-adapters-registry__label">Adapter Registry</p>
              <p className="gn-adapters-registry__desc">
                Live adapter registry entries.
              </p>
            </div>
            <div className="gn-adapters-registry__stats">
              <span>{registryEntries.length} entries</span>
            </div>
          </div>
          <div className="gn-adapters-registry__grid">
            {registryEntries.map((entry) => {
              const providerMeta = getProvidersForDomain(entry.domain).find(
                (provider) => provider.id === entry.provider,
              );

              return (
                <div
                  key={`${entry.domain}-${entry.provider}`}
                  className="gn-adapters-registry__item"
                >
                  <div className="gn-adapters-registry__info">
                    <p className="gn-adapters-registry__domain">
                      {DOMAIN_LABELS[entry.domain]}
                    </p>
                    <p className="gn-adapters-registry__provider">
                      {providerMeta?.name ?? entry.provider}
                    </p>
                  </div>
                  <AdapterStatusBadge status={entry.status} />
                </div>
              );
            })}
          </div>
        </section>
      )}

      <div className="gn-adapters-filters">
        <button
          type="button"
          onClick={() => setFilterStatus(undefined)}
          aria-pressed={!filterStatus}
          className={cx(
            "gn-adapters-filter-btn",
            !filterStatus && "gn-adapters-filter-btn--active",
          )}
        >
          All
        </button>
        {ADAPTER_STATUSES.map((s) => (
          <button
            key={s}
            type="button"
            onClick={() => setFilterStatus(s)}
            aria-pressed={filterStatus === s}
            className={cx(
              "gn-adapters-filter-btn",
              filterStatus === s && "gn-adapters-filter-btn--active",
            )}
          >
            <AdapterStatusBadge status={s} showLabel={false} />
            {s}
          </button>
        ))}
      </div>

      <section aria-labelledby={adapterDomainsHeadingId}>
        <h2 id={adapterDomainsHeadingId} className="gn-adapters-sr-only">
          Adapter domains
        </h2>
        <AdapterList
          onSelect={(domain) => {
            if (onNavigateDetail) {
              onNavigateDetail(domain);
            } else {
              setSelectedDomain(domain);
            }
          }}
          filterStatus={filterStatus}
        />
      </section>

      {selectedDomain && (
        <div className="gn-adapters-modal-overlay">
          <button
            type="button"
            aria-label="Close adapter configuration"
            className="gn-adapters-modal-backdrop"
            onClick={() => setSelectedDomain(null)}
          />
          <div
            ref={modalRef}
            className="gn-adapters-modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby={modalTitleId}
            tabIndex={-1}
          >
            <AdapterConfigForm
              domain={selectedDomain}
              titleId={modalTitleId}
              initialConfig={resolvedConfigByDomain?.[selectedDomain]}
              onSave={async (config) => {
                await onConfigure?.(selectedDomain, config);
                setSelectedDomain(null);
              }}
              onCancel={() => setSelectedDomain(null)}
            />
          </div>
        </div>
      )}
    </div>
  );
}
